import React, { useEffect, useState } from "react";
import { FaEdit, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import ConfirmationModal from '../../components/modals/ConfirmationModal';
import Toast from '../../components/Toast/Toast';
import { getBrandsAPI, addBrandAPI, updateBrandAPI, deleteBrandAPI } from '../../utils/api';
import Sidebar from "../Sidebar/Sidebar";
import AddBrandModal from "./components/AddBrandModal";
import "./Brand.css";

const Brand = () => {
  const [brands, setBrands] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [brandName, setBrandName] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [brandToDelete, setBrandToDelete] = useState(null);

  const PAGE_LIMIT = 10;

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 3000);
  };

  const fetchBrands = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getBrandsAPI({
        page,
        limit: PAGE_LIMIT,
        search: searchTerm.trim().toLowerCase() || "",
      });

      if (response?.status === 200) {
        const list = response.data;
        const totalPagesFromApi = response?.pagination?.total_pages || 1;
        const currentPageFromApi = response?.pagination?.current_page || page;
        setBrands(list);
        setTotalPages(totalPagesFromApi);
        if (currentPageFromApi !== page) setPage(currentPageFromApi);
      } else {
        setError(response?.message || 'Failed to fetch brands');
      }
    } catch (err) {
      console.log('Fetch error:', err);
      setError('Failed to fetch brands. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, [page, searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const normalizedSearch = searchTerm.toLowerCase().trim();
  const totalEntries = brands.length;
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * PAGE_LIMIT;
  const endIdx = startIdx + brands.length;

  const handleEdit = (brand) => {
    setSelectedBrand(brand);
    setBrandName(brand.name);
    setIsEditMode(true);
    setShowModal(true);
  };

  const handleAddNew = () => {
    setSelectedBrand(null);
    setBrandName("");
    setIsEditMode(false);
    setShowModal(true);
    setSaveLoading(false);
  };

  const handleSave = async () => {
    if (!brandName.trim()) {
      showToast("Please enter a brand name.", "error");
      return;
    }

    setSaveLoading(true);

    try {
      if (isEditMode && selectedBrand) {
        const reqData = {
          brand_id: selectedBrand._id,
          name: brandName.trim()
        };

        const response = await updateBrandAPI(reqData);

        if (response?.status === 200) {
          await fetchBrands();
          setShowModal(false);
          setBrandName("");
          setIsEditMode(false);
          setSelectedBrand(null);
          showToast("Brand updated successfully!", "success");
        } else {
          showToast(response?.message || 'Failed to update brand', "error");
        }
      } else {
        const reqData = {
          name: brandName.trim()
        };

        const response = await addBrandAPI(reqData);

        if (response?.status === 200) {
          await fetchBrands();
          setShowModal(false);
          setBrandName("");
          setIsEditMode(false);
          setSelectedBrand(null);
          showToast("Brand added successfully!", "success");
        } else {
          showToast(response?.message || 'Failed to add brand', "error");
        }
      }
    } catch (err) {
      console.error('Save error:', err);
      showToast('An error occurred. Please try again.', "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setBrandName("");
    setIsEditMode(false);
    setSelectedBrand(null);
    setSaveLoading(false);
  };

  const handleDeleteClick = (brand) => {
    setBrandToDelete(brand);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (!brandToDelete?._id) {
        showToast("Invalid brand data", "error");
        return;
      }

      const reqData = {
        brand_id: brandToDelete._id
      };

      const response = await deleteBrandAPI(reqData);

      if (response?.status === 200) {
        setShowDeleteModal(false);
        setBrandToDelete(null);
        showToast(`Brand "${brandToDelete?.name}" deleted successfully!`, "success");
        await fetchBrands();
      } else {
        showToast(response?.message || 'Failed to delete brand', "error");
      }
    } catch (err) {
      console.log('Delete error:', err);
      showToast('An error occurred. Please try again.', "error");
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setBrandToDelete(null);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField === field) {
      return sortOrder === "asc" ? <FaSortUp /> : <FaSortDown />;
    }
    return <FaSort />;
  };

  const getSortedData = () => {
    const sorted = [...brands].sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      // Convert to string for comparison
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      
      if (sortOrder === "asc") {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
    return sorted;
  };

  const sortedBrands = getSortedData();

  const getPageItems = () => {
    const items = [];
    const current = Math.max(1, Math.min(page, totalPages));
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) items.push(i);
      return items;
    }

    items.push(1);
    const showLeftDots = current > 4;
    const showRightDots = current < totalPages - 3;

    if (showLeftDots) items.push('dots-left');

    const start = Math.max(2, current - 1);
    const end = Math.min(totalPages - 1, current + 1);
    for (let i = start; i <= end; i++) items.push(i);

    if (showRightDots) items.push('dots-right');

    items.push(totalPages);
    return items;
  };

  return (
    <>
      <Toast toast={toast} />

      <div className="Brand-layout">
        <Sidebar />
        <div className="Brand-main-content">
          <div className="Brand-header-row">
            <input
              type="text"
              className="search-input"
              placeholder="Search by brand name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search brands"
            />
            <button
              type="button"
              className="insert-btn"
              onClick={handleAddNew}
              aria-label="Add Brand"
            >
              <span>Add Brand</span>
            </button>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading brands...</p>
            </div>
          ) : error ? (
            <div className="loading-container">
              <div style={{ color: '#dc2626', fontSize: '1.25rem', marginBottom: '1rem' }}>⚠️</div>
              <p style={{ color: '#dc2626', fontSize: '1.125rem' }}>{error}</p>
            </div>
          ) : totalEntries === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏷️</div>
              <h3>No Brands Found</h3>
              <p>There are no brands in the system yet.</p>
              <p>Click "Add Brand" to create the first one.</p>
            </div>
          ) : brands.length === 0 ? (
            <div className="empty-state">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔎</div>
              <h3>No results</h3>
              <p>We couldn't find any brand matching "{searchTerm}".</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="Brand-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th 
                        className="sortable-header"
                        onClick={() => handleSort("name")}
                      >
                        <div className="header-content">
                          Brand Name
                          <span className="sort-icon">
                            {getSortIcon("name")}
                          </span>
                        </div>
                      </th>
                      <th className="actions-col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedBrands.map((brand, idx) => (
                      <tr key={brand._id || `brand-${idx}`}>
                        <td>{startIdx + idx + 1}</td>
                        <td>{brand.name || ''}</td>
                        <td>
                          <div className="brand-action-btn-row">
                            <button
                              type="button"
                              className="brand-action-btn brand-edit-btn"
                              title="Edit Brand"
                              onClick={() => handleEdit(brand)}
                              aria-label="Edit"
                            >
                              <FaEdit />
                            </button>
                            <button
                              type="button"
                              className="brand-action-btn brand-delete-btn"
                              title="Delete Brand"
                              onClick={() => handleDeleteClick(brand)}
                              aria-label="Delete"
                            >
                              <MdDelete />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="table-footer">
                <div className="footer-left">
                  <span>
                    Showing {brands.length > 0 ? startIdx + 1 : 0} to {endIdx} • Page {page} of {totalPages}
                  </span>
                </div>
                <div className="pagination">
                  <button
                    type="button"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                    aria-label="Previous page"
                  >
                    Previous
                  </button>
                  {getPageItems().map((item, idx) => (
                    typeof item === 'number' ? (
                      <button
                        type="button"
                        key={`p-${item}`}
                        className={page === item ? 'active' : ''}
                        onClick={() => handlePageChange(item)}
                        aria-current={page === item ? 'page' : undefined}
                      >
                        {item}
                      </button>
                    ) : (
                      <span key={`dots-${idx}`} className="ellipsis" aria-hidden="true">…</span>
                    )
                  ))}
                  <button
                    type="button"
                    disabled={page === totalPages}
                    onClick={() => handlePageChange(page + 1)}
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Add/Edit Modal */}
          <AddBrandModal
            showModal={showModal}
            isEditMode={isEditMode}
            brandName={brandName}
            setBrandName={setBrandName}
            saveLoading={saveLoading}
            onSave={handleSave}
            onClose={handleCloseModal}
          />

          <ConfirmationModal
            visible={showDeleteModal}
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            title="Confirm Deletion"
            message={`Are you sure you want to delete the Brand "${brandToDelete?.name || ''}"?`}
            confirmText="Delete"
          />
        </div>
      </div>
    </>
  );
};

export default Brand; 